import React from 'react';
import { Home, Person, AttachMoney } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';

export interface VerificationIndicatorsProps {
  housingVerified: boolean;
  identityVerified: boolean;
  incomeVerified: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const VerificationIndicators: React.FC<VerificationIndicatorsProps> = ({
  housingVerified,
  identityVerified,
  incomeVerified,
  size = 'medium',
  className,
}) => {
  const getIconSize = () => {
    switch (size) {
      case 'small':
        return 16;
      case 'large':
        return 24;
      default:
        return 20;
    }
  };

  const getContainerSize = () => {
    switch (size) {
      case 'small':
        return 24;
      case 'large':
        return 36;
      default:
        return 32;
    }
  };

  const iconSize = getIconSize();
  const containerSize = getContainerSize();

  const indicators = [
    {
      verified: housingVerified,
      icon: <Home sx={{ fontSize: iconSize }} />,
      tooltip: housingVerified ? 'Housing Verified' : 'Housing Not Verified',
    },
    {
      verified: identityVerified,
      icon: <Person sx={{ fontSize: iconSize }} />,
      tooltip: identityVerified ? 'Identity Verified' : 'Identity Not Verified',
    },
    {
      verified: incomeVerified,
      icon: <AttachMoney sx={{ fontSize: iconSize }} />,
      tooltip: incomeVerified ? 'Income Verified' : 'Income Not Verified',
    },
  ];

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {indicators.map((indicator, index) => (
        <Tooltip key={index} title={indicator.tooltip} arrow>
          <Box
            sx={{
              width: containerSize,
              height: containerSize,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: indicator.verified ? '#dcfce7' : '#e5e7eb', // green-100 for verified, gray-200 for unverified to match Figma
              color: indicator.verified ? '#16a34a' : '#6b7280', // green-600 for verified, gray-500 for unverified
              transition: 'all 0.2s ease-in-out',
              cursor: 'help',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {indicator.icon}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};
